import { logUsage } from 'owa-analytics';
import {
    AttachmentFile,
    AttachmentFileList,
    AttachmentFileType,
    CloudFile,
} from 'owa-attachment-file-types';
import { LinkIdContainer } from 'owa-attachment-link-common';
import { ShareAttachmentProperties } from 'owa-attachment-well-data';
import EditorViewState from 'owa-editor/lib/store/schema/EditorViewState';
import {
    createSharingLinkFromFileProvider,
    CreateSharingLinkFromFileProviderResult,
} from 'owa-fileprovider-link-services';
import { getGuid } from 'owa-guid';
import { preloadLinkData, RecipientContainer } from 'owa-link-data';
import sleep from 'owa-sleep';
import * as trace from 'owa-trace';
import { addInsertLinkViewState, onSharingLinkRetrivalFailure } from '../actions/internalActions';
import { createLinkPlaceholder } from '../components/LinkPlaceholder';
import { InsertLinkFailureType } from '../types/InsertLinkFailureType';
import { addEditorUndoSnapshot } from '../utils/addEditorUndoSnapshot';
import { OWA_INSERT_LINK_PLACEHOLDER } from '../utils/constants';
import { replacePlaceholderWithLink } from './replacePlaceholderWithLink';
import { uploadLocalFileToOneDrive } from './uploadLocalFileToOneDrive';

function getInsertLinkFailureTypeFromStatusCode(statusCode: number): InsertLinkFailureType {
    switch (statusCode) {
        case 403:
            return InsertLinkFailureType.InsufficientFilePermissions;
        default:
            return InsertLinkFailureType.GenericFailure;
    }
}

export async function insertLinkIntoComposeEditor(
    viewState: EditorViewState,
    getRecipientWells: () => RecipientContainer[],
    fromAddress: string,
    linkIdContainer: LinkIdContainer,
    file: AttachmentFile,
    placeholderId: string,
    viaContextMenuItem: boolean,
    targetWindow: Window,
    displayText: string,
    upsellProperties: ShareAttachmentProperties
) {
    let createLinkResult: CreateSharingLinkFromFileProviderResult;
    let cloudFile: CloudFile;
    try {
        switch (file.fileType) {
            case AttachmentFileType.Cloud:
            case AttachmentFileType.ContextualSuggestion:
            case AttachmentFileType.CloudSuggestion:
                cloudFile = file;
                break;
            case AttachmentFileType.Local:
                cloudFile = await uploadLocalFileToOneDrive(file, upsellProperties, placeholderId);
                break;
            default:
                trace.errorThatWillCauseAlert(
                    `[insertLinkIntoComposeEditor] unsupported fileType: ${file.fileType}`
                );
                break;
        }

        createLinkResult = await createSharingLinkFromFileProvider(
            cloudFile.providerType,
            cloudFile.fileId,
            cloudFile.location,
            cloudFile.isFolder,
            cloudFile.providerEndpointUrl
        );
    } catch (e) {
        // TODO VSO 80693: replace with perf datapoint
        const error = e as Error;
        let errorObj;
        try {
            errorObj = JSON.parse(error.toString());
        } catch (err) {
            // Only some code throw the JSON error while JSON.parse would throw for all the other general errors .
        }

        const statusCode = errorObj?.responseStatus;
        logUsage('InsertLinksIntoComposeDone', {
            success: false,
            viaContextMenuItem: viaContextMenuItem,
            providerType: cloudFile?.providerType,
            statusCode: statusCode,
        });

        const insertLinkFailureType: InsertLinkFailureType = getInsertLinkFailureTypeFromStatusCode(
            statusCode
        );
        onSharingLinkRetrivalFailure(placeholderId, insertLinkFailureType);
        return;
    }

    // TODO VSO 80693: replace with perf datapoint
    logUsage('InsertLinksIntoComposeDone', {
        success: true,
        viaContextMenuItem: viaContextMenuItem,
        providerType: cloudFile.providerType,
    });
    replacePlaceholderWithLink(
        createLinkResult,
        cloudFile,
        placeholderId,
        viewState,
        getRecipientWells,
        fromAddress,
        linkIdContainer,
        targetWindow,
        displayText
    );
}

export async function insertLinksIntoComposeEditor(
    viewState: EditorViewState,
    files: AttachmentFileList,
    linkIdContainer: LinkIdContainer,
    getRecipientWells: () => RecipientContainer[],
    fromAddress: string,
    targetWindow: Window,
    selectionText: string, // This is for hyperlink entry scenario where user selects typed text.
    upsellProperties: ShareAttachmentProperties
) {
    if (files.length === 0) {
        return;
    }

    await addEditorUndoSnapshot(viewState);

    // Insert link placeholders
    const placeholderIds: string[] = [];
    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const placeholderId = OWA_INSERT_LINK_PLACEHOLDER + getGuid();
        placeholderIds.push(placeholderId);
        addInsertLinkViewState(placeholderId, file, upsellProperties, selectionText);

        // TODO VSO 80693: replace with perf datapoint
        const cloudFile: CloudFile = file.fileType === AttachmentFileType.Cloud ? file : null;
        logUsage('InsertLinksIntoComposeStart', { providerType: cloudFile?.providerType });

        // Using the sleep to make sure we set the cursor position rightly
        await sleep(0);
        await createLinkPlaceholder(
            viewState,
            placeholderId,
            getRecipientWells,
            linkIdContainer,
            fromAddress,
            null /*originalPlaceHolder*/,
            targetWindow,
            files.length > 1 /*shouldDisplayInNextLine*/
        );
    }

    await preloadLinkData.import();

    // Retrieve the sharing link to replace the placeholder with beautiful link
    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        await insertLinkIntoComposeEditor(
            viewState,
            getRecipientWells,
            fromAddress,
            linkIdContainer,
            file,
            placeholderIds[i],
            false /* viaContextMenuItem */,
            targetWindow,
            selectionText,
            upsellProperties
        );
    }

    await addEditorUndoSnapshot(viewState);
}
